import mixpanel from "mixpanel-browser";
import { useEffect, useState } from "react";

import { Activity, currentRequestTimeStamp } from "../../../Constants";
import { useAppContext } from "../../../Context/AppContext";
import { useAuctionList } from "../../../hooks/useAuctionList";
import { Urls } from "../../../Urls";
import AuctionTable from "./AuctionsTable/AuctionsTable";
import styles from "./LandingPageV2.module.css";
import { useHistory } from "react-router-dom";
import SlideDrawer from "../../Shared/SlideDrawer/SlideDrawer";
import AuctionFilterOptions from "./AuctionFilterOptions";
import { useAuctionFilterMeta } from "../../../hooks/useAuctionFilterMeta";
import FreightBreakups from "./FreightBreakups";
import PopupWidget from "../../PopupWidget";
import ControlledPopupModal from "../../Shared/PopupModal/ControlledPopupModal/ControlledPopupModal";
import { useMixPanelForTracking } from "../../../shared_utilities/trackMixPanel";

const getFilterCount = (filters = {}) => {
  let count = 0;
  let dateFilterCounted = false;

  for (const key in filters) {
    const val = filters[key];

    if (key === "start_date" || key === "end_date") {
      // Skip individual date keys — handle both together once
      if (!dateFilterCounted && filters.start_date && filters.end_date) {
        count++;
        dateFilterCounted = true;
      }
      continue;
    }

    if (Array.isArray(val) && val.length > 0) count++;
    else if (typeof val === "string" && val.trim() !== "") count++;
  }

  return count;
};

const debounce = (fn, delay) => {
  let timeoutId;
  const debounced = (...args) => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
  debounced.cancel = () => clearTimeout(timeoutId);
  return debounced;
};

export const AUCTION_STATUS_MAP = {
  inactive: "Upcoming",
  active: "Live",
  paused: "Paused",
  closed: "Closed",
};

export default function LandingPageV2() {
  const [auctionBucket, setAuctionBucket] = useState("active");
  const [hasAutoSwitchedBucket, setHasAutoSwitchedBucket] = useState(false);
  const [freightBreakupPopup, setFreightBreakupPopup] = useState({
    auction_id: "",
    showPopup: false,
  });
  const { transporterInfo } = useAppContext();
  const {TrackMixPanel} = useMixPanelForTracking()
  const [filters, setFilters] = useState({});
  const { data: metaOptions, isLoading: isMetaLoading } = useAuctionFilterMeta(
    auctionBucket,
    transporterInfo.transporter_id
    // filters
  );

  const { data, isLoading, isFetching, error, refetch } = useAuctionList(
    auctionBucket,
    filters
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const activeFilterCount = getFilterCount(filters);

  const filteredAuctions = (data?.auctions || []).filter((auc) => {
    const query = debouncedSearch.toLowerCase();
    return (
      auc?.display_id?.toLowerCase().includes(query) ||
      auc?.auction_description?.toLowerCase().includes(query) ||
      auc?.procurement_name?.toLowerCase().includes(query) ||
      auc?.company_name?.toLowerCase().includes(query)
    );
  });

  const history = useHistory();

  const handleNavigateAuction = (auction) => {
    TrackMixPanel({
      Definition:"user has clicked on the view button",
      ModuleName:"tpmp auction",
      ScreenName:`auctions - ${auction?.auction_status} auctions`,
      Data:{
        "AuctionId":auction?.auction_id,
        "AuctionDescription":auction?.auction_description,
        "ProcurementName":auction?.procurement_name,
        "Type":auction?.type
      }
    })
    if (auction.auction_status === "inactive") {
      return;
    } else if (auction.auction_status === "closed") {
      if (auction.type === "advanced" || (!auction?.lot_no)) {
        history.push({
          pathname: "/history",
          state: { details: auction },
        });
        return;
      }
      setFreightBreakupPopup({
        auction_id: auction.auction_id,
        showPopup: true,
      });
    } else {
      const route =
        auction.type === "advanced"
          ? `/Advanced_Bidding/${auction.auction_id}`
          : `/Bidding/${auction.auction_id}`;
      history.push({
        pathname: route,
        state: {
          auction_id: auction.auction_id,
          procurement_id: auction.procurement_id,
          display_id: auction.display_id,
          bucket: auctionBucket,
        },
      });
    }
  };

  const handleBucketChange = (bucket) => {
    TrackMixPanel({
      ModuleName:"tpmp auction",
      Data:"None",
      ScreenName:"auctions",
      Definition:`user has clicked on the ${bucket} bucket.`
    })
    if (bucket !== auctionBucket) {
      setAuctionBucket(bucket);
    }
  };

  const closeFreightBreakupPopup = () => {
    setFreightBreakupPopup({
      auction_id: "",
      showPopup: false,
    });
  };

  // Automatic fallback if "active" has no auctions
  useEffect(() => {
    if (
      !hasAutoSwitchedBucket &&
      auctionBucket === "active" &&
      !isLoading &&
      !isFetching &&
      data?.auctions?.length === 0
    ) {
      setAuctionBucket("inactive");
      setHasAutoSwitchedBucket(true);
    }
  }, [auctionBucket, isLoading, data]);

  useEffect(() => {
    const handler = debounce((val) => {
      setDebouncedSearch(val);
    }, 400);

    handler(searchTerm);

    return () => {
      handler.cancel && handler.cancel(); // if cancel support is added
    };
  }, [searchTerm]);

  useEffect(() => {

    document.title = "Auction - Auction Summary";

    Activity([
      {
        screen: "Auction",
        module: "Auction Summary Page",
        user_activity: `Auction Summary Page Viewed`,
        timestamp: currentRequestTimeStamp(),
      },
    ]);

    // mixpanel.track("User has viewed auction page", {
    //   ApplicationName: `${Urls.appnamemixpanel}`,
    //   ScreenName: "Transporter-Profile",
    //   ModuleName: "live-tenders-rfq-auction",
    //   ActivityLog: "userViewedAuctionPage",
    //   Data: `None`,
    //   Definition: "User has viewed auction page",
    //   TransporterName: transporterInfo.transporter_name,
    //   TransporterId: transporterInfo.transporter_id,
    //   ActivityType: "page_view",
    // });

    TrackMixPanel({
      ModuleName:"tpmp auction",
      Definition:"user has viewed auction page",
      ScreenName:`auctions`,
      Data:"None"
    })

  }, []);

  return (
    <div className={`${styles.auctionPageContainer}`}>
      {/* header */}
      <div className="d-flex justify-content-between">
        <div className={styles.pageHeader} onClick={refetch}>
          <div className={styles.pageTitle}>Auctions</div>
          <button className={`${styles.refreshButton}`}>
            <i
              className={`fa fa-refresh ${isFetching ? "fa-spin" : ""}`}
              aria-hidden="true"
            ></i>
          </button>
        </div>

        <div className="d-flex">
          {activeFilterCount > 0 && (
            <div className={styles.activeFilterBar}>
              <span>
                {activeFilterCount} filter{activeFilterCount > 1 ? "s" : ""}{" "}
                applied
              </span>
              <button
                onClick={() => setFilters({})}
                className={styles.clearLink}
              >
                Clear All
              </button>
            </div>
          )}
        </div>
      </div>

      {/* auction */}
      <div>
        {/* table utils */}
        <div className={`${styles.tableUtilBar}`}>
          {/* buckets */}
          <div className={styles.bucketButtonContainer}>
            {Object.entries(AUCTION_STATUS_MAP).map(([key, label]) => (
              <button
                key={key}
                className={`${styles.bucketButton} ${
                  auctionBucket === key ? styles.activeBucket : ""
                }`}
                onClick={() => handleBucketChange(key)}
              >
                {label}
              </button>
            ))}
          </div>

          {/* search */}
          <div className={styles.searchContainer}>
            <i className="fa fa-search" aria-hidden="true"></i>
            <input
              type="text"
              placeholder="Search Auctions"
              className={styles.searchInput}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* filters */}
          <div className={styles.filterContainer}>
            <SlideDrawer
              showCloseButton={true}
              triggerButton={
                <button className={styles.filterButton}>
                  <i className="fa fa-filter" aria-hidden="true"></i>
                  <span>
                    Advanced Filters
                    {activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
                  </span>
                  <i className="fa fa-angle-down" aria-hidden="true"></i>
                </button>
              }
              defaultOpen={false}
              width="26vw"
              customStyles={{ overlay: { backgroundColor: "#F1EFEF66" } }}
            >
              <AuctionFilterOptions
                options={metaOptions}
                filters={filters}
                auctionBucket={auctionBucket}
                setFilters={setFilters}
              />
            </SlideDrawer>
          </div>
        </div>

        {/* table */}
        <div className="pb-5">
          <AuctionTable
            auctions={filteredAuctions}
            isLoading={isLoading}
            error={error}
            bucket={auctionBucket}
            refetch={refetch}
            onNavigateAuction={handleNavigateAuction}
          />
        </div>

        <div>
          <ControlledPopupModal
            title={"Post Auction Lane wise Freight Breakups"}
            content={
              <FreightBreakups auctionID={freightBreakupPopup?.auction_id} />
            }
            backdrop={true}
            screen={`Auction`}
            modulename={`Auction`}
            id={`closed-modal-${freightBreakupPopup.auction_id}`}
            size="xl"
            isOpen={freightBreakupPopup?.showPopup}
            overflowStyles={{
              zIndex: "1000001",
            }}
            onClose={closeFreightBreakupPopup}
          />
        </div>
      </div>
    </div>
  );
}
